import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { DownloadProgressItem } from '../hooks/useDownloadManager';

interface DownloadProgressProps {
  progress: DownloadProgressItem[];
  error: string | null;
  isGeneratingZip: boolean;
  onDismissError: () => void;
  onClearCompleted: () => void;
}

export const DownloadProgress = ({
  progress,
  error,
  isGeneratingZip,
  onDismissError,
  onClearCompleted,
}: DownloadProgressProps) => {
  if (!progress.length && !error) return null;

  const hasCompleted = progress.some((item) => item.status === 'complete');
  const inProgress = progress.some(
    (item) => item.status === 'fetching' || item.status === 'zipping',
  );

  return (
    <div className="py-1 px-2 border-b border-border">
      {error && (
        <div className="flex items-center justify-between bg-red-50 text-red-800 p-1 mb-1 rounded text-xs">
          <span>{error}</span>
          <button
            type="button"
            className="ml-2 text-red-600 hover:text-red-800"
            onClick={onDismissError}
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        </div>
      )}

      {isGeneratingZip && (
        <div className="py-1 px-2 text-xs bg-blue-50 text-blue-700 rounded mb-1">
          Generating zip file...
        </div>
      )}

      {progress.map((item, index) => (
        <div key={`${item.fileName}-${index}`} className="py-0.5 text-[11px]">
          <div className="flex justify-between items-center">
            <span className="truncate max-w-[180px]">{item.fileName}</span>
            <span className="ml-2 flex-shrink-0">
              {item.status === 'pending' && 'Pending'}
              {item.status === 'fetching' && 'Downloading'}
              {item.status === 'zipping' && 'Packaging'}
              {item.status === 'complete' && (
                <CheckIcon className="h-3 w-3 text-green-600" />
              )}
              {item.status === 'error' && (
                <span className="text-red-600">Failed</span>
              )}
            </span>
          </div>
          {(item.status === 'fetching' || item.status === 'zipping') && (
            <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden mt-0.5">
              <div
                className="h-full bg-blue-500 rounded-full"
                style={{ width: `${item.progress}%` }}
              />
            </div>
          )}
        </div>
      ))}

      {hasCompleted && !inProgress && (
        <div className="flex justify-end mt-1">
          <button
            type="button"
            className="text-[10px] text-muted-foreground hover:text-foreground"
            onClick={onClearCompleted}
          >
            Clear completed
          </button>
        </div>
      )}
    </div>
  );
};
